const PROBE_TIMEOUT_MS = 6000;
const FUNCTIONS_REGION = process.env.REACT_APP_FIREBASE_REGION || 'europe-west1';

function clean(value, max = 120) {
  return String(value ?? '')
    .replace(/AIza[0-9A-Za-z_-]{20,}/g, '[api-key]')
    .replace(/[^\s@]+@[^\s@]+\.[^\s@]+/g, '[email]')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);
}

function getConnectionDiagnostics() {
  if (typeof window === 'undefined') return {};
  const nav = window.navigator || {};
  const connection = nav.connection || nav.mozConnection || nav.webkitConnection || {};
  return {
    online: nav.onLine !== false,
    cookieEnabled: nav.cookieEnabled === true,
    effectiveType: clean(connection.effectiveType, 20),
    downlink: Number.isFinite(Number(connection.downlink)) ? Number(connection.downlink) : null,
    rtt: Number.isFinite(Number(connection.rtt)) ? Number(connection.rtt) : null,
    saveData: connection.saveData === true,
    visibilityState: clean(document.visibilityState, 20),
  };
}

function summarizeProbeResult(result) {
  return Object.entries(result)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => `${key}=${clean(value, 80)}`)
    .join('; ');
}

async function probeUrl(url, body, label, base) {
  const startedAt = Date.now();
  const controller = base.abortAvailable ? new AbortController() : null;
  const timeoutId = controller
    ? setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)
    : null;

  try {
    const response = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      cache: 'no-store',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
      },
      body,
      signal: controller?.signal,
    });
    const elapsedMs = Date.now() - startedAt;
    let responseCode = '';
    try {
      const data = await response.clone().json();
      responseCode = data?.error?.message || '';
    } catch (err) {
      responseCode = err?.name || 'unreadable-response';
    }

    return {
      [`${label}Probe`]: 'completed',
      [`${label}Reachable`]: true,
      [`${label}Status`]: response.status,
      [`${label}Ok`]: response.ok,
      [`${label}ResponseCode`]: responseCode,
      [`${label}ElapsedMs`]: elapsedMs,
    };
  } catch (err) {
    return {
      [`${label}Probe`]: 'failed',
      [`${label}Reachable`]: false,
      [`${label}ErrorName`]: err?.name || 'Error',
      [`${label}ErrorMessage`]: err?.message || String(err),
      [`${label}ElapsedMs`]: Date.now() - startedAt,
    };
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

export async function collectAuthNetworkDiagnostics() {
  const apiKey = process.env.REACT_APP_FIREBASE_API_KEY || '';
  const authDomain = process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '';
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || '';
  const base = {
    authDomain,
    projectId,
    fetchAvailable: typeof fetch === 'function',
    abortAvailable: typeof AbortController === 'function',
    ...getConnectionDiagnostics(),
  };

  if (!base.fetchAvailable || !apiKey) {
    return summarizeProbeResult({
      ...base,
      probe: apiKey ? 'fetch-unavailable' : 'missing-api-key',
    });
  }

  const body = JSON.stringify({
    email: 'diagnostic@member-nest.invalid',
    password: 'diagnostic-password',
    returnSecureToken: true,
  });
  const identityUrl = `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${encodeURIComponent(apiKey)}`;
  const proxyUrl = projectId
    ? `https://${FUNCTIONS_REGION}-${projectId}.cloudfunctions.net/authProxy/v1/accounts:signInWithPassword?key=${encodeURIComponent(apiKey)}`
    : '';

  const identityProbe = await probeUrl(identityUrl, body, 'identity', base);
  const proxyProbe = proxyUrl
    ? await probeUrl(proxyUrl, body, 'proxy', base)
    : { proxyProbe: 'missing-project-id' };

  return summarizeProbeResult({
    ...base,
    ...identityProbe,
    ...proxyProbe,
  });
}
